import React, { useState, useEffect } from "react";
import {
  Drawer,
  Button,
  Input,
  Form,
  Space,
  Avatar,
  Popconfirm,
  List,
  Spin,
  Row,
  Col,
  Tooltip,
  Typography,
  Checkbox,
  Select,
  Tag,
  Card,
  Modal,
  Skeleton,
  Collapse,
  Menu,
  Dropdown,
} from "antd";
import {
  UserOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  TeamOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import { message } from "antd";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchGroupMembers,
  addGroupMember,
  updateGroupMember,
  removeGroupMember,
  bulkAddGroupMembers,
} from "../../store/groupMemberSlice";
import { fetchUserGroupsByUserId } from "../../store/groupSlice";

const { Title, Text } = Typography;
const { Option } = Select;
const { Panel } = Collapse;
const ChatDrawer = ({
  visible,
  onClose,
  group,
  onEditGroupName,
  onDeleteGroup,
}) => {
  const dispatch = useDispatch();
  const { members, loading, error } = useSelector(
    (state) => state.groupMembers
  );
  const getInitials = (name) => {
    if (!name) return "";
    const words = name.trim().split(" ");
    if (words.length === 1) return words[0][0].toUpperCase();
    return (words[0][0] + words[1][0]).toUpperCase();
  };
  const [newGroupName, setNewGroupName] = useState(group?.name || "");
  const [newMemberId, setNewMemberId] = useState("");
  const [roleInGroup, setRoleInGroup] = useState(1);
  const [permissions, setPermissions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editMember, setEditMember] = useState(null);
  const [canManageMembers, setCanManageMembers] = useState(false);
  const [isGroupAdmin, setIsGroupAdmin] = useState(false);
  const [userGroups, setUserGroups] = useState([]);
  const [selectedUserGroupId, setSelectedUserGroupId] = useState(null);
  const [previewMembers, setPreviewMembers] = useState([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [selectedMemberIds, setSelectedMemberIds] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [groupSearchTerm, setGroupSearchTerm] = useState("");
  const [memberSearchTerm, setMemberSearchTerm] = useState("");

  useEffect(() => {
    const storedUser = localStorage.getItem("userInfo");
    const user = JSON.parse(storedUser);

    if (visible && group?.id) {
      dispatch(fetchGroupMembers(group.id));
    }

    if (user && members.length > 0) {
      const currentMember = members.find((m) => m.userId === user.userId);
      setCanManageMembers(currentMember?.canManageMembers || false);
      setIsGroupAdmin(currentMember?.roleInGroup === 0);
    }
  }, [visible, group?.id, members]);

  useEffect(() => {
    if (group?.name) setNewGroupName(group.name);
  }, [group]);

  const handleEditGroupName = () => {
    if (newGroupName.trim() && newGroupName !== group?.name) {
      onEditGroupName(newGroupName.trim());
    } else {
      message.info("No changes made to group name.");
    }
  };

  // pull current userId from localStorage
  const userId = JSON.parse(localStorage.getItem("userInfo"))?.userId;

  // ── When the Add‐Member modal opens, grab all groups ──
  useEffect(() => {
    if (addModalVisible && userId) {
      dispatch(fetchUserGroupsByUserId(userId))
        .unwrap()
        .then((groups) => setUserGroups(groups))
        .catch(() => setUserGroups([]));
    }
  }, [addModalVisible, userId, dispatch]);

  // ── Whenever the selected group changes, fetch its members ──
  useEffect(() => {
    if (selectedGroupIds.length) {
      setMembersLoading(true);
      // fetch each group's members in parallel, simulate a small network delay
      Promise.all(
        selectedGroupIds.map((gid) => dispatch(fetchGroupMembers(gid)).unwrap())
      )
        .then((arrays) => {
          // flatten and dedupe by userId
          const all = arrays.flat();
          const uniq = Array.from(
            new Map(all.map((m) => [m.userId, m])).values()
          );
          setPreviewMembers(uniq);
        })
        .catch(() => setPreviewMembers([]))
        .finally(() => {
          // give user a sense of loading
          setTimeout(() => setMembersLoading(false), 300);
        });
    } else {
      setPreviewMembers([]);
    }
  }, [selectedGroupIds, dispatch]);

  const handleDeleteGroup = () => {
    onDeleteGroup();
  };

  const handleAddMember = async () => {
    if (!newMemberId.trim()) {
      message.warning("Please enter a user ID.");
      return;
    }

    try {
      await dispatch(
        addGroupMember({
          userGroupId: group?.userGroupId,
          userId: newMemberId.trim(),
          roleInGroup,
          canReply: permissions.includes("canReply"),
          canCreate: permissions.includes("canCreate"),
          canManageMembers: permissions.includes("canManageMembers"),
        })
      ).unwrap();

      message.success("Member added successfully");
      setNewMemberId("");
      setPermissions([]);
      setRoleInGroup(1);
      setAddModalVisible(false);
    } catch (err) {
      message.error(err || "Failed to add member");
    }
  };

  const openEditModal = (member) => {
    setEditMember(member);
    setPermissions(
      [
        member.canReply && "canReply",
        member.canCreate && "canCreate",
        member.canManageMembers && "canManageMembers",
      ].filter(Boolean)
    );
    setRoleInGroup(member.roleInGroup);
    setEditModalVisible(true);
  };

  const handleUpdateMember = async () => {
    if (!editMember) return;

    try {
      await dispatch(
        updateGroupMember({
          id: editMember.groupMemberId,
          updatedData: {
            roleInGroup,
            canReply: permissions.includes("canReply"),
            canCreate: permissions.includes("canCreate"),
            canManageMembers: permissions.includes("canManageMembers"),
          },
        })
      ).unwrap();
      message.success("Member updated successfully.");
      setEditModalVisible(false);
    } catch (err) {
      message.error(err || "Failed to update member.");
    }
  };

  // Add a new handler inside ChatDrawer, alongside handleAddMember:
  const handleBulkAdd = async () => {
    if (!selectedGroupIds.length || !selectedMemberIds.length) {
      message.warning("Please select at least one group and one member.");
      return;
    }

    // build the bulk payload
    const bulkPayload = {
      bulkGroupMembers: [
        {
          userGroupId: group.userGroupID,
          userIds: selectedMemberIds,
          roleInGroup,
          canCreate: permissions.includes("canCreate"),
          canManageMembers: permissions.includes("canManageMembers"),
          canReply: permissions.includes("canReply"),
        },
      ],
    };

    try {
      const result = await dispatch(bulkAddGroupMembers(bulkPayload)).unwrap();
      const successCount = result.successfullyAddedMembers.length;
      const errorCount = result.errors.length;
      message.success(`Added ${successCount} members; ${errorCount} errors.`);
      // reset state
      setSelectedGroupIds([]);
      setSelectedMemberIds([]);
      setPermissions([]);
      setRoleInGroup(1);
      setAddModalVisible(false);
    } catch (err) {
      message.error(err || "Bulk add failed");
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!canManageMembers) {
      message.warning("You don't have permission to remove members.");
      return;
    }

    try {
      await dispatch(removeGroupMember(memberId)).unwrap();
      message.success("Member removed successfully.");
    } catch (err) {
      message.error(err || "Failed to remove member");
    }
  };

  const filteredMembers = members.filter(
    (member) =>
      member.userName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.userId?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      <Drawer
        title={
          <Space>
            <TeamOutlined />
            <span>Group Details</span>
          </Space>
        }
        placement="right"
        open={visible}
        onClose={onClose}
        width={420}
        zIndex={1000}
        footer={
          <Space style={{ float: "right" }}>
            <Button onClick={onClose}>Close</Button>
          </Space>
        }
      >
        {group ? (
          <>
            <Row justify="center" align="middle" style={{ marginBottom: 24 }}>
              <Col>
                <Avatar
                  size={80}
                  style={{
                    marginBottom: 8,
                    backgroundColor: "#87d068",
                    fontSize: 28,
                    fontWeight: "bold",
                  }}
                >
                  {getInitials(group.name)}
                </Avatar>
                <Title level={4} style={{ textAlign: "center", margin: 0 }}>
                  {group.name}
                </Title>
                <Text type="secondary">{members.length} members</Text>
              </Col>
            </Row>

            <Form layout="vertical">
              {isGroupAdmin && (
                <Form.Item label="Group Name">
                  <Input
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                    placeholder="Enter new group name"
                  />
                </Form.Item>
              )}

              {isGroupAdmin && (
                <Row gutter={12} justify="center">
                  <Col>
                    <Button
                      type="primary"
                      icon={<EditOutlined />}
                      onClick={handleEditGroupName}
                      disabled={
                        !newGroupName.trim() || newGroupName === group.name
                      }
                    >
                      Edit
                    </Button>
                  </Col>
                  <Col>
                    <Popconfirm
                      title="Are you sure to delete this group?"
                      onConfirm={handleDeleteGroup}
                      okText="Yes"
                      cancelText="No"
                    >
                      <Tooltip color="red" placement="top">
                        <Button type="primary" danger icon={<DeleteOutlined />}>
                          Delete
                        </Button>
                      </Tooltip>
                    </Popconfirm>
                  </Col>
                </Row>
              )}

              {canManageMembers && (
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={() => setAddModalVisible(true)}
                  block
                  style={{ marginTop: 24 }}
                >
                  Add Member
                </Button>
              )}
            </Form>

            <Input.Search
              placeholder="Search members"
              allowClear
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ marginTop: 24 }}
            />

            {loading ? (
              <Spin tip="Loading Members..." style={{ marginTop: 24 }} />
            ) : error ? (
              <Text type="danger">{error}</Text>
            ) : (
              <List
                header={<strong>Group Members</strong>}
                bordered
                dataSource={filteredMembers}
                style={{ marginTop: 16 }}
                renderItem={(member) => (
                  <List.Item
                    key={member.groupMemberId}
                    extra={
                      canManageMembers && (
                        <Dropdown
                          overlay={
                            <Menu>
                              <Menu.Item
                                icon={<EditOutlined />}
                                onClick={() => openEditModal(member)}
                              >
                                Edit Member
                              </Menu.Item>
                              <Menu.Item
                                icon={<DeleteOutlined />}
                                onClick={() =>
                                  handleRemoveMember(member.groupMemberId)
                                }
                              >
                                Remove Member
                              </Menu.Item>
                            </Menu>
                          }
                        >
                          <Button
                            type="link"
                            icon={<MoreOutlined style={{ color: "green" }} />}
                          />
                        </Dropdown>
                      )
                    }
                  >
                    <List.Item.Meta
                      avatar={
                        <Avatar
                          icon={<UserOutlined />}
                          style={{ backgroundColor: "#108ee9" }}
                        />
                      }
                      title={member.userName || `User #${member.userId}`}
                      description={
                        <>
                          <div>
                            Role:{" "}
                            <Text type="secondary">
                              {member.roleInGroup === 0 ? "Admin" : "Member"}
                            </Text>
                          </div>
                          <div>
                            {member.canReply && <Tag color="blue">Reply</Tag>}
                            {member.canCreate && (
                              <Tag color="green">Create</Tag>
                            )}
                            {member.canManageMembers && (
                              <Tag color="red">Manage</Tag>
                            )}
                          </div>
                        </>
                      }
                    />
                  </List.Item>
                )}
              />
            )}
          </>
        ) : (
          <Text>No group selected.</Text>
        )}
      </Drawer>

      {/* Add Member Modal */}

      <Modal
        title="Add Member"
        open={addModalVisible}
        onCancel={() => setAddModalVisible(false)}
        onOk={handleBulkAdd}
        okText="Add Member"
        confirmLoading={loading}
        width={800}
        zIndex={1000}
        bodyStyle={{ padding: 24 }}
      >
        <Form layout="vertical">
          <Card
            bordered={false}
            style={{
              borderRadius: 16,
              boxShadow: "0 6px 16px rgba(0,0,0,0.12)",
              marginBottom: 24,
            }}
            bodyStyle={{ padding: 0 }}
          >
            <Collapse
              defaultActiveKey={["groups"]}
              ghost
              accordion
              expandIconPosition="end"
            >
              {/* ── Groups Panel ── */}
              <Panel
                header={
                  <Space>
                    <TeamOutlined />
                    <Text strong>Select Groups</Text>
                  </Space>
                }
                key="groups"
                style={{ background: "#fafafa" }}
              >
                <Input.Search
                  placeholder="Search groups"
                  allowClear
                  onChange={(e) => setGroupSearchTerm(e.target.value)}
                  style={{ margin: "0 16px 16px" }}
                />
                <div
                  style={{
                    maxHeight: 240,
                    overflowY: "auto",
                    padding: "0 16px 16px",
                  }}
                >
                  <Checkbox.Group
                    value={selectedGroupIds}
                    onChange={(checked) => {
                      setSelectedGroupIds(checked);
                      const last = checked[checked.length - 1];
                      if (last) {
                        dispatch(fetchGroupMembers(last))
                          .unwrap()
                          .then((ms) => setPreviewMembers(ms))
                          .catch(() => setPreviewMembers([]));
                      } else {
                        setPreviewMembers([]);
                      }
                    }}
                  >
                    <Space
                      direction="vertical"
                      size="middle"
                      style={{ width: "100%" }}
                    >
                      {userGroups
                        .filter((g) =>
                          g.name
                            .toLowerCase()
                            .includes(groupSearchTerm.toLowerCase())
                        )
                        .map((g) => (
                          <Card
                            key={g.userGroupID}
                            size="small"
                            hoverable
                            style={{
                              margin: 0,
                              border: selectedGroupIds.includes(g.userGroupID)
                                ? "2px solid #1890ff"
                                : "1px solid #f0f0f0",
                              borderRadius: 12,
                              transition: "all 0.3s",
                            }}
                            bodyStyle={{ padding: 12 }}
                          >
                            <Checkbox
                              value={g.userGroupID}
                              style={{ width: "100%" }}
                            >
                              <Space align="center">
                                <Avatar size={28} icon={<TeamOutlined />} />
                                <Text
                                  strong
                                  ellipsis
                                  style={{ marginLeft: 12 }}
                                >
                                  {g.name}
                                </Text>
                              </Space>
                            </Checkbox>
                          </Card>
                        ))}
                    </Space>
                  </Checkbox.Group>
                </div>
              </Panel>

              {/* ── Members Panel ── */}
              <Panel
                header={
                  <Space>
                    <UserOutlined />
                    <Text strong>Select Members</Text>
                  </Space>
                }
                key="members"
                style={{ background: "#fafafa" }}
              >
                <Input.Search
                  placeholder="Search members"
                  allowClear
                  disabled={!selectedGroupIds.length}
                  onChange={(e) => setMemberSearchTerm(e.target.value)}
                  style={{ margin: "0 16px 16px" }}
                />

                <Skeleton active loading={membersLoading}>
                  <div
                    style={{
                      maxHeight: 240,
                      overflowY: "auto",
                      padding: "0 16px 16px",
                    }}
                  >
                    <Checkbox.Group
                      value={selectedMemberIds}
                      onChange={setSelectedMemberIds}
                      disabled={!selectedGroupIds.length}
                    >
                      <Space
                        direction="vertical"
                        size="middle"
                        style={{ width: "100%" }}
                      >
                        {previewMembers
                          .filter((m) =>
                            m.userName
                              .toLowerCase()
                              .includes(memberSearchTerm.toLowerCase())
                          )
                          .map((m) => (
                            <Card
                              key={m.userId}
                              size="small"
                              hoverable
                              style={{
                                margin: 0,
                                border: selectedMemberIds.includes(m.userId)
                                  ? "2px solid #52c41a"
                                  : "1px solid #f0f0f0",
                                borderRadius: 12,
                                transition: "all 0.3s",
                              }}
                              bodyStyle={{ padding: 12 }}
                            >
                              <Checkbox
                                value={m.userId}
                                style={{ width: "100%" }}
                              >
                                <Space align="center">
                                  <Avatar size={28} icon={<UserOutlined />} />
                                  <Text
                                    strong
                                    ellipsis
                                    style={{ marginLeft: 12 }}
                                  >
                                    {m.userName}
                                  </Text>
                                </Space>
                              </Checkbox>
                            </Card>
                          ))}
                      </Space>
                    </Checkbox.Group>
                  </div>
                </Skeleton>
              </Panel>
            </Collapse>
          </Card>

          <Form.Item label="Role in Group">
            <Select
              value={roleInGroup}
              onChange={(value) => setRoleInGroup(value)}
            >
              <Option value={0}>Admin</Option>
              <Option value={1}>Member</Option>
            </Select>
          </Form.Item>

          <Form.Item label="Permissions">
            <Checkbox.Group value={permissions} onChange={setPermissions}>
              <Row gutter={[8, 8]}>
                <Col span={24}>
                  <Checkbox value="canReply">Can Reply</Checkbox>
                </Col>
                <Col span={24}>
                  <Checkbox value="canCreate">Can Create</Checkbox>
                </Col>
                <Col span={24}>
                  <Checkbox value="canManageMembers">
                    Can Manage Members
                  </Checkbox>
                </Col>
              </Row>
            </Checkbox.Group>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Member Modal */}
      <Modal
        title="Edit Member"
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        okText="Update Member"
        zIndex={1000}
        onOk={() =>
          Modal.confirm({
            title: "Confirm Update",
            content: "Are you sure you want to apply these changes?",
            onOk: handleUpdateMember,
          })
        }
        confirmLoading={loading}
      >
        <Form layout="vertical">
          <Form.Item label="Role in Group">
            <Select
              value={roleInGroup}
              onChange={(value) => setRoleInGroup(value)}
            >
              <Option value={0}>Admin</Option>
              <Option value={1}>Member</Option>
            </Select>
          </Form.Item>

          <Form.Item label="Permissions">
            <Checkbox.Group
              value={permissions}
              onChange={setPermissions}
              style={{ width: "100%" }}
            >
              <Row gutter={[8, 8]}>
                <Col span={24}>
                  <Checkbox value="canReply">Can Reply</Checkbox>
                </Col>
                <Col span={24}>
                  <Checkbox value="canCreate">Can Create</Checkbox>
                </Col>
                <Col span={24}>
                  <Checkbox value="canManageMembers">
                    Can Manage Members
                  </Checkbox>
                </Col>
              </Row>
            </Checkbox.Group>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default ChatDrawer;
